import React, { useEffect, useState } from 'react';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import { stagesApi } from 'api/deals';

/**
 * Sprint 15 QA — pipeline stage management (Owner/Manager).
 * Add / rename / probability / won-lost flags / delete. Terminal stages are
 * flagged; at most one won and one lost; a stage with deals can't be deleted.
 */
export default function PipelineStagesModal({ isOpen, stages, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState('');
  const [newProbability, setNewProbability] = useState('');
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setRows((stages || []).map((s) => ({ id: s.id, name: s.name, probability: s.probability, isWon: s.isWon, isClosed: s.isClosed })));
      setNewName('');
      setNewProbability('');
      setError('');
    }
  }, [isOpen, stages]);

  if (!isOpen) return null;

  const setRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setError('');
  };

  const saveRow = async (row) => {
    setSavingId(row.id);
    setError('');
    try {
      const patch = { name: row.name, probability: Number(row.probability) };
      if (row.isWon) patch.isWon = true;
      else if (row.isClosed) patch.isClosed = true;
      else patch.isWon = false;
      await stagesApi.updateStage(row.id, patch);
      await onChanged?.();
    } catch (e) {
      setError(e?.message || 'Could not update the stage.');
    } finally {
      setSavingId(null);
    }
  };

  const removeRow = async (row) => {
    setSavingId(row.id);
    setError('');
    try {
      await stagesApi.removeStage(row.id);
      await onChanged?.();
    } catch (e) {
      setError(e?.message || 'Could not delete the stage.');
    } finally {
      setSavingId(null);
    }
  };

  const addRow = async () => {
    if (!newName.trim()) {
      setError('Stage name is required.');
      return;
    }
    setSavingId('new');
    setError('');
    try {
      await stagesApi.addStage(newName.trim(), newProbability);
      setNewName('');
      setNewProbability('');
      await onChanged?.();
    } catch (e) {
      setError(e?.message || 'Could not add the stage.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold text-gray-800">Pipeline Stages</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={18} color="currentColor" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Add, rename or delete stages. Won/lost stages are terminal — close actions move deals there. A stage holding deals can't be deleted.
        </p>

        {error && (
          <div className="mb-3 rounded-lg border px-3 py-2 text-sm" style={{ borderColor: '#FCA5A5', background: '#FEF2F2', color: '#B91C1C' }}>
            {error}
          </div>
        )}

        <div className="space-y-3 mb-4">
          {rows.map((row) => (
            <div key={row.id} className="rounded-lg border p-3" style={{ borderColor: '#E5E7EB' }}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-400 w-5">{row.id}.</span>
                <input
                  value={row.name}
                  onChange={(e) => setRow(row.id, { name: e.target.value })}
                  className="flex-1 rounded-md border px-2 py-1.5 text-sm outline-none focus:border-blue-500"
                  style={{ borderColor: '#D1D5DB' }}
                  maxLength={40}
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={row.probability}
                  onChange={(e) => setRow(row.id, { probability: e.target.value })}
                  className="w-16 rounded-md border px-2 py-1.5 text-sm outline-none focus:border-blue-500"
                  style={{ borderColor: '#D1D5DB' }}
                  title="Probability %"
                />
                <span className="text-xs text-gray-400">%</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-3 text-xs">
                  {['open', 'won', 'lost'].map((kind) => {
                    const active =
                      kind === 'open' ? !row.isWon && !row.isClosed : kind === 'won' ? row.isWon : row.isClosed;
                    return (
                      <button
                        key={kind}
                        type="button"
                        onClick={() =>
                          setRow(row.id, {
                            isWon: kind === 'won',
                            isClosed: kind === 'won' || kind === 'lost',
                          })
                        }
                        className="px-2 py-1 rounded-full font-medium"
                        style={{
                          background: active ? '#DBEAFE' : '#F3F4F6',
                          color: active ? '#1D4ED8' : '#6B7280',
                        }}
                      >
                        {kind === 'open' ? 'Open' : kind === 'won' ? 'Won' : 'Lost'}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => saveRow(row)}
                    disabled={savingId === row.id || !row.name.trim()}
                  >
                    {savingId === row.id ? 'Saving...' : 'Save'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => removeRow(row)}
                    disabled={savingId === row.id}
                    className="flex items-center justify-center rounded-md"
                    style={{ width: 28, height: 28, color: '#DC2626', background: '#FEF2F2' }}
                    title="Delete stage"
                  >
                    <Icon name="Trash2" size={14} color="currentColor" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-dashed p-3" style={{ borderColor: '#D1D5DB' }}>
          <p className="text-xs font-semibold text-gray-500 mb-2">Add a new stage</p>
          <div className="flex items-center gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Stage name (e.g. On Hold)"
              className="flex-1 rounded-md border px-2 py-1.5 text-sm outline-none focus:border-blue-500"
              style={{ borderColor: '#D1D5DB' }}
              maxLength={40}
            />
            <input
              type="number"
              min="0"
              max="100"
              value={newProbability}
              onChange={(e) => setNewProbability(e.target.value)}
              placeholder="%"
              className="w-16 rounded-md border px-2 py-1.5 text-sm outline-none focus:border-blue-500"
              style={{ borderColor: '#D1D5DB' }}
            />
            <Button variant="default" size="sm" iconName="Plus" iconPosition="left" iconSize={13} onClick={addRow} disabled={savingId === 'new'}>
              {savingId === 'new' ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
